import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit, computed } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { VoteResultsCategory } from '../../../models/voteResultsCategory';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-vote-results-category-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './vote-results-category-list.component.html',
  styleUrl: './vote-results-category-list.component.scss'
})
export class VoteResultsCategoryListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  voteResultsCategories = signal<VoteResultsCategory[]>([]);
  rightItem: VoteResultsCategory | undefined = undefined;
  matchedItems: VoteResultsCategory[] | undefined = undefined;

  // Computed columns that update when categories change
  voteResultsCategoriesColumns = computed<ColumnConfig[]>(() => {
    const parentCategories = this.voteResultsCategories().filter(c => c.parentId === null);
    return [
      { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
      {
        key: 'parentId',
        header: 'Parent',
        type: 'select',
        sortable: true,
        editable: true,
        selectOptions: [
          { value: null as any, label: '(None)' },
          ...parentCategories.map(c => ({ value: c.id, label: c.name }))
        ]
      }
    ];
  });

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadVoteResultsCategories();
  }

  loadVoteResultsCategories(): void {
    this.spinnerService.show();
    this.lutService.getVoteResultsCategories().subscribe({
      next: (data) => {
        const sortedData = this.sortVoteResultsCategories(data);
        this.voteResultsCategories.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading vote results categories:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortVoteResultsCategories(categories: VoteResultsCategory[]): VoteResultsCategory[] {
    return [...categories].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateVoteResultsCategory(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(voteResultsCategory: VoteResultsCategory): void {
  }

  onEditVoteResultsCategory(voteResultsCategory: VoteResultsCategory): void {
  }

  onDeleteVoteResultsCategory(voteResultsCategory: VoteResultsCategory): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(voteResultsCategory.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalVoteResultsCategories = this.voteResultsCategories();

          // 1. Optimistically remove from local state immediately
          this.voteResultsCategories.update(categories => categories.filter(c => c.id !== voteResultsCategory.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteVoteResultsCategory(voteResultsCategory.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Vote results category deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting vote results category:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.voteResultsCategories.set(originalVoteResultsCategories);
              this.toast.error(`<strong>Failed to delete Vote Results Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: VoteResultsCategory, newName: string} | {item: VoteResultsCategory, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: VoteResultsCategory;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      if (event.columnKey === 'parentId') {
        // When updating parentId, also update parentName
        const newParentId = event.newValue;
        let newParentName: string | null = null;

        if (newParentId) {
          const parent = this.voteResultsCategories().find(c => c.id === newParentId);
          newParentName = parent ? parent.name : null;
        }

        updatedItem = {
          ...event.item,
          parentId: newParentId,
          parentName: newParentName
        };
      } else {
        updatedItem = { ...event.item, [event.columnKey]: event.newValue };
      }
    }

    // 1. Optimistically update local state immediately and re-sort
    this.voteResultsCategories.update(categories => {
      const updated = categories.map(c => c.id === event.item.id ? updatedItem : c);
      return this.sortVoteResultsCategories(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateVoteResultsCategory(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Vote results category updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.voteResultsCategories.update(categories => {
          const updated = categories.map(c => c.id === event.item.id ? response : c);
          return this.sortVoteResultsCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error updating vote results category:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.voteResultsCategories.update(categories =>
          categories.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update Vote Results Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();

    // Get parentId and parentName from the event
    const parentId = event['parentId'] || null;
    let parentName: string | null = null;

    // If a parent was selected, find its name
    if (parentId) {
      const parent = this.voteResultsCategories().find(c => c.id === parentId);
      parentName = parent ? parent.name : null;
    }

    const newVoteResultsCategory: VoteResultsCategory = {
      id: tempId,
      name: event['name'],
      parentId: parentId,
      parentName: parentName,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.voteResultsCategories.update(categories => this.sortVoteResultsCategories([...categories, newVoteResultsCategory]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createVoteResultsCategory({ name: event['name'], parentId: parentId, parentName: parentName, systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Vote results category created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.voteResultsCategories.update(categories => {
          const updated = categories.map(c => c.id === tempId ? response : c);
          return this.sortVoteResultsCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error creating vote results category:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.voteResultsCategories.update(categories =>
          categories.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Vote Results Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: VoteResultsCategory, matchedItems?: VoteResultsCategory[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onVoteResultsCategoryCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalVoteResultsCategories = this.voteResultsCategories();

        // 1. Optimistically remove merged items from local state
        this.voteResultsCategories.update(categories =>
          categories.filter(c => !bulkMergeRequest.leftIds.includes(c.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeVoteResultsCategoriesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Vote results categories merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadVoteResultsCategories();
          },
          error: (error) => {
            console.error('Error merging vote results categories:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.voteResultsCategories.set(originalVoteResultsCategories);
            // Restore selection state for retry
            this.rightItem = originalVoteResultsCategories.find(c => c.id === bulkMergeRequest.rightId);
            this.matchedItems = originalVoteResultsCategories.filter(c => bulkMergeRequest.leftIds.includes(c.id));
            this.toast.error(`<strong>Failed to merge Vote Results Categories:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
